'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Reveal } from '../Reveal'
import { CheckIcon, ArrowIcon } from '../Icons'
import { submitContact } from '@/lib/api'
import { projectTypes, budgetRanges, contact } from '@/lib/site-config'

interface QuoteState {
  name: string
  phone: string
  projectType: string
  location: string
  area: string
  service: string
  budget: string
  details: string
}

const initial: QuoteState = {
  name: '',
  phone: '',
  projectType: '',
  location: '',
  area: '',
  service: '',
  budget: '',
  details: '',
}

type Status = 'idle' | 'loading' | 'success' | 'error'

function sanitizeDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function QuoteForm(): React.ReactElement {
  const [form, setForm] = useState<QuoteState>(initial)
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  function update<K extends keyof QuoteState>(key: K, value: string): void {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')

    const digits = sanitizeDigits(form.phone) || '000000000'
    const message = [
      `طلب عرض سعر جديد`,
      `الاسم: ${form.name}`,
      `رقم الهاتف: ${form.phone}`,
      `نوع المشروع: ${form.projectType || 'غير محدد'}`,
      `موقع المشروع: ${form.location || 'غير محدد'}`,
      `المساحة التقريبية: ${form.area || 'غير محدد'}`,
      `الخدمات المطلوبة: ${form.service || 'غير محدد'}`,
      `الميزانية التقريبية: ${form.budget || 'غير محدد'}`,
      `تفاصيل المشروع: ${form.details || 'لا يوجد'}`,
    ].join('\n')

    try {
      await submitContact({
        name: form.name,
        email: `quote+${digits}@abusalehcontracting.com`,
        subject: `طلب عرض سعر - ${form.projectType || 'مشروع'}`,
        company: form.location || undefined,
        message,
      })
      setStatus('success')
      setForm(initial)
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'حدث خطأ غير متوقع')
    }
  }

  return (
    <section id="quote" className="section-pad relative overflow-hidden bg-charcoal-900">
      <div className="absolute inset-0 grid-overlay opacity-30" />
      <div className="container-x relative">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="eyebrow mb-5">طلب عرض سعر</span>
          <h2 className="font-heading text-3xl font-extrabold text-white sm:text-4xl md:text-5xl">
            احصل على <span className="gold-text">عرض سعر</span> لمشروعك
          </h2>
          <p className="mt-5 text-lg text-charcoal-300">
            املأ النموذج التالي وسنتواصل معك في أقرب وقت ممكن.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mx-auto mt-12 max-w-3xl">
          <div className="card-surface p-7 sm:p-10">
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center py-10 text-center"
                >
                  <span className="mb-5 inline-flex h-20 w-20 items-center justify-center rounded-full bg-gold-gradient text-charcoal-950 shadow-gold">
                    <CheckIcon className="h-10 w-10" strokeWidth={2.5} />
                  </span>
                  <h3 className="font-heading text-2xl font-extrabold text-white">
                    تم استلام طلبك
                  </h3>
                  <p className="mt-3 max-w-md text-charcoal-300">
                    تم استلام طلبك، وسنتواصل معك في أقرب وقت ممكن.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="btn-outline mt-8"
                  >
                    إرسال طلب آخر
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={handleSubmit}
                  className="grid gap-5 sm:grid-cols-2"
                >
                  <div>
                    <label className="field-label" htmlFor="q-name">
                      الاسم الكامل *
                    </label>
                    <input
                      id="q-name"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      className="field-input"
                      placeholder="الاسم الكامل"
                    />
                  </div>

                  <div>
                    <label className="field-label" htmlFor="q-phone">
                      رقم الهاتف *
                    </label>
                    <input
                      id="q-phone"
                      required
                      type="tel"
                      inputMode="tel"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      className="field-input"
                      placeholder="7XX XXX XXX"
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="field-label" htmlFor="q-type">
                      نوع المشروع
                    </label>
                    <select
                      id="q-type"
                      value={form.projectType}
                      onChange={(e) => update('projectType', e.target.value)}
                      className="field-input"
                    >
                      <option value="">اختر نوع المشروع</option>
                      {projectTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="field-label" htmlFor="q-location">
                      موقع المشروع
                    </label>
                    <input
                      id="q-location"
                      value={form.location}
                      onChange={(e) => update('location', e.target.value)}
                      className="field-input"
                      placeholder="المدينة / الحي"
                    />
                  </div>

                  <div>
                    <label className="field-label" htmlFor="q-area">
                      المساحة التقريبية
                    </label>
                    <input
                      id="q-area"
                      value={form.area}
                      onChange={(e) => update('area', e.target.value)}
                      className="field-input"
                      placeholder="مثال: 200 متر مربع"
                    />
                  </div>

                  <div>
                    <label className="field-label" htmlFor="q-service">
                      الخدمات المطلوبة
                    </label>
                    <input
                      id="q-service"
                      value={form.service}
                      onChange={(e) => update('service', e.target.value)}
                      className="field-input"
                      placeholder="مثال: بناء + تشطيب"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="q-budget">
                      الميزانية التقريبية
                    </label>
                    <select
                      id="q-budget"
                      value={form.budget}
                      onChange={(e) => update('budget', e.target.value)}
                      className="field-input"
                    >
                      <option value="">اختر الميزانية التقريبية</option>
                      {budgetRanges.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="q-details">
                      تفاصيل المشروع
                    </label>
                    <textarea
                      id="q-details"
                      rows={4}
                      value={form.details}
                      onChange={(e) => update('details', e.target.value)}
                      className="field-input resize-none"
                      placeholder="اكتب أي تفاصيل إضافية عن مشروعك..."
                    />
                  </div>

                  {status === 'error' && (
                    <p className="sm:col-span-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                      {errorMsg || 'تعذّر إرسال الطلب، يرجى المحاولة مرة أخرى.'}
                    </p>
                  )}

                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {status === 'loading' ? 'جارٍ الإرسال...' : 'إرسال طلب عرض السعر'}
                      {status !== 'loading' && <ArrowIcon className="h-5 w-5" />}
                    </button>
                    <p className="mt-4 text-center text-xs text-charcoal-500">
                      أو تواصل مباشرة عبر واتساب:{' '}
                      <a
                        href={`https://wa.me/${contact.whatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-gold-300 hover:underline"
                      >
                        {contact.whatsappDisplay}
                      </a>
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
